import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { useSubmitFPS } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetDashboardPaymentListParams } from '@nxsys/store/slices/dashboard/types';
import { SubmitFPSParams } from '@nxsys/types/api/dashboard';
import { ICompany } from '@nxsys/types/models/company';
import { IPayroll } from '@nxsys/types/models/payroll';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { getBackendMessage } from '@nxsys/utils/getErrorMessage';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find, range, toNumber } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { getShownPensionValue } from '@nxsys/utils/reported-pension';
import { useDispatch, useSelector } from 'react-redux';
import SubmitFinalFPSModal, {
  SubmitFinalFPSValues,
} from './SubmitFinalFPSModal';

const NormalFPS: React.FC = () => {
  const dispatch = useDispatch();
  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);
  const { paymentList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = paymentList;

  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [selectedTaxYear, setSelectedTaxYear] = useState<number | null>(currentTaxYear);
  const [selectedTaxWeek, setSelectedTaxWeek] = useState<any>(null);
  const [isFinalSubmission, setIsFinalSubmission] = useState(false);
  const [openModalFinalSubmit, setOpenModalFinalSubmit] = useState(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      companyPaymentId: selectedCompany?.id,
      taxYear: selectedTaxYear || undefined,
      taxWeek: selectedTaxWeek?.value || undefined,
    });
  }, [selectedCompany, selectedTaxYear, selectedTaxWeek]);

  const handleFetchData = (params: GetDashboardPaymentListParams) => {
    const newParams = {
      ...params,
      type: 'FPS',
    };

    dispatch(dashboardActions.getDashboardPaymentList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const columns: ColumnsType<IPayroll> = [
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id || '-',
    },
    {
      key: 'subcontractorRef',
      title: 'Employee Name',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      dataIndex: 'subcontractorRef',
      render: (value) =>
        value?.fullName || `${value?.surname || '-'} ${value?.forename || ''}`,
    },
    ...(!isBureau
      ? [
          {
            title: 'UTR No',
            dataIndex: 'subcontractorRef',
            render: (value: any) => value?.setting?.UTRNo || '-',
          },
          {
            title: 'Personal Verification No',
            dataIndex: 'subcontractorRef',
            render: (value: any) => value?.setting?.personalVerificationNumber || '-',
          },
        ]
      : []),
    {
      title: 'Payment Company ID',
      dataIndex: 'companyRefId',
      render: (value) => value || '-',
    },

    {
      key: 'gross',
      title: 'Gross',
      dataIndex: 'gross',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)) || '-',
    },
    ...(!isBureau
      ? [
          {
            key: 'materials',
            title: 'Materials',
            dataIndex: 'materials',
            align: 'right' as const,
            render: (value: any) =>
              value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
          },
          {
            key: 'cisDeduction',
            title: 'CIS Deduction',
            dataIndex: 'cisDeduction',
            align: 'right' as const,
            render: (value: any) =>
              value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
          },
        ]
      : []),
  ];

  const taxWeekOptions = range(1, 55).map((week) => ({
    label: `Tax Week ${week}`,
    value: week,
  }));

  const handleExport = () => {
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }
    const dataExport = list.map((item) => {
      const {
        payFrequency,
        subcontractorRef,
        payeeUmbrellaValue,
        net,
        studentLoan,
        postgraduateLoan,
        otherPayment,
        otherDeduction,
      } = item;
      return {
        previousPayrollId: subcontractorRef?.setting?.previousPayrollNo || '',
        agencyReference: `${subcontractorRef?.primaryAgency?.agencyPrefix || '___'
          }/${subcontractorRef?.agencyRef || ''}`,
        payFrequency:
          find(defaultPaymentFrequency, (item) => item?.value === payFrequency)
            ?.label || '',
        forename: subcontractorRef?.forename || '',
        surname: subcontractorRef?.surname || '',
        grossForTax: formatMoneyExport(
          payeeUmbrellaValue?.grossForTaxAndNI,
          showCurrencyByCountry(payrollCountry),
        ),
        grossForNI: formatMoneyExport(
          payeeUmbrellaValue?.grossForTaxAndNI,
          showCurrencyByCountry(payrollCountry),
        ),
        employersNI: formatMoneyExport(
          payeeUmbrellaValue?.employersNI,
          showCurrencyByCountry(payrollCountry),
        ),
        employeeNI: formatMoneyExport(
          payeeUmbrellaValue?.employeeNI,
          showCurrencyByCountry(payrollCountry),
        ),
        tax: formatMoneyExport(
          payeeUmbrellaValue?.tax,
          showCurrencyByCountry(payrollCountry),
        ),
        // Same split the FPS sends HMRC: salary sacrifice is employer money.
        employersPension: formatMoneyExport(
          getShownPensionValue(item, 'employersPension'),
          showCurrencyByCountry(payrollCountry),
        ),
        employeePension: formatMoneyExport(
          getShownPensionValue(item, 'employeePension'),
          showCurrencyByCountry(payrollCountry),
        ),
        appLevy: formatMoneyExport(
          payeeUmbrellaValue?.appLevy,
          showCurrencyByCountry(payrollCountry),
        ),
        net: formatMoneyExport(
          toNumber(net),
          showCurrencyByCountry(payrollCountry),
        ),
        studentLoan: formatMoneyExport(
          studentLoan,
          showCurrencyByCountry(payrollCountry),
        ),
        postgraduateLoan: formatMoneyExport(
          postgraduateLoan,
          showCurrencyByCountry(payrollCountry),
        ),
        otherPayment: formatMoneyExport(
          toNumber(otherPayment),
          showCurrencyByCountry(payrollCountry),
        ),
        otherDeduction: formatMoneyExport(
          toNumber(otherDeduction),
          showCurrencyByCountry(payrollCountry),
        ),
      };
    });
    const headers = [
      'Previous Payroll Number',
      'Agency Reference',
      'Pay Frequency',
      'Forename',
      'Surname',
      'Gross for Tax',
      'Gross for NI',
      'Employer NI',
      'Employee NI',
      'Tax',
      'Employer Pension',
      'Employee Pension',
      'Apprenticeship Levy',
      'Net Pay',
      'Student Loan',
      'Postgraduate Student Loan',
      'Other Payments',
      'Other Deductions',
    ];
    const csvExporter = new ExportToCsv({
      filename:
        `FPS-${moment(new Date()).tz('Europe/London').format('DD/MM/YYYY')}` ||
        'Download.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const handleClickSubmit = () => {
    if (!selectedCompany || !selectedTaxYear || !selectedTaxWeek) {
      showMessage({
        type: 'warning',
        content: 'Please select company, tax year and tax week',
      });
      return;
    }
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }
    if (isFinalSubmission) {
      setOpenModalFinalSubmit(true);
    } else {
      handleSubmitFPS();
    }
  };
  const { run: submitFPS, loading: submitFPSLoading } = useSubmitFPS();
  const handleSubmitFPS = async () => {
    const params: SubmitFPSParams = {
      companyId: selectedCompany?.id || '',
      taxYear: selectedTaxYear || 0,
      taxWeek: selectedTaxWeek?.value,
      finalSubmitType: null,
      dateSchemeCeased: null,
      isTesting: import.meta.env.VITE_IS_TESTING_API === 'true',
    };
    const res = await submitFPS(params);
    if (res?.success) {
      showMessage({
        type: 'success',
        content: 'Submit FPS successfully',
      });
    } else {
      showMessage({
        type: 'error',
        content: getBackendMessage(res) || 'Submit FPS failed',
      });
    }
    refetchData();
    window.dispatchEvent(new CustomEvent('dashboard:reload-rti'));
  };
  const handleSubmitFinalFPS = async (values: SubmitFinalFPSValues) => {
    const params: SubmitFPSParams = {
      companyId: selectedCompany?.id || '',
      taxYear: selectedTaxYear || 0,
      taxWeek: selectedTaxWeek?.value,
      finalSubmitType: values?.finalSubmitType?.value,
      dateSchemeCeased: values?.dateSchemeCeased?.format('DD/MM/YYYY') || null,
      isTesting: import.meta.env.VITE_IS_TESTING_API === 'true',
    };
    const res = await submitFPS(params);
    if (res?.success) {
      showMessage({
        type: 'success',
        content: 'Submit FPS successfully',
      });
    } else {
      showMessage({
        type: 'error',
        content: getBackendMessage(res) || 'Submit FPS failed',
      });
    }
    refetchData();
    window.dispatchEvent(new CustomEvent('dashboard:reload-rti'));
  };

  return (
    <div className="NormalFPS">
      {submitFPSLoading && <AppLoader />}
      <AppsContent>
        <div className="mt-[20px] px-6 mb-[20px]">
          <AppCheckbox
            checked={isFinalSubmission}
            onChange={(e) => setIsFinalSubmission(e?.target?.checked)}
          >
            Final Submission?
          </AppCheckbox>
        </div>
        <div className="flex items-center justify-between gap-2 w-full mb-[20px] flex-wrap px-6">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder="Select Payment Company"
              onChangeValue={(value: any) => {
                setSelectedCompany(value?.fullData);
              }}
              paramFilter={{ enableRTI: true }}
            />
            <TaxYearSelect
              placeholder="Select Tax Year"
              onFilter={(e) => setSelectedTaxYear(e?.taxYear || null)}
              allowClear
            />
            <AppSelect
              options={taxWeekOptions}
              placeholder="Select Tax Week"
              className="!min-w-0 !w-fit"
              onChange={(value) => setSelectedTaxWeek(value)}
            />
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'submit',
                    label: 'Submit',
                    onClick: handleClickSubmit,
                  },
                  {
                    key: 'export',
                    label: 'Export',
                    onClick: handleExport,
                  },
                ],
              }}
            >
              <AppButton name="Actions" rightIcon={<DownOutlined />} />
            </Dropdown>
          </div>
          <AppInputSearch onChange={handleSearch} />
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2000 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
      <SubmitFinalFPSModal
        isOpen={openModalFinalSubmit}
        toggle={() => setOpenModalFinalSubmit(false)}
        onSubmit={handleSubmitFinalFPS}
      />
    </div>
  );
};

export default NormalFPS;
